import { useState, useEffect, useCallback, useRef } from "react";
import { getAiConfig, getPerplexityConfig, isAiConfigured, isPerplexityConfigured } from "./kisApi";
import "./styles/ticker.css";
import { Ico } from "./lib/icons";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// ========== 공통 상수 ==========
const SRC_COLOR = { '한국경제': '#f0c040', '한국경제 증권': '#f0c040', '연합뉴스 경제': '#6da0f2', 'CNBC': '#38bdf8', 'WSJ': '#94a3b8', 'Investing.com': '#f97316', 'FinancialJuice': '#22c55e', 'Axios': '#a78bfa' };
const srcColor = (s) => SRC_COLOR[s] || (s && /경제|신문|일보|뉴스|서울|매일|조선|중앙|머니투데이|아시아|파이낸셜/.test(s) ? '#34d399' : '#a78bfa');

const timeAgo = (d) => {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (isNaN(m) || m < 0) return '';
  if (m < 1) return '방금';
  if (m < 60) return `${m}분전`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}시간전` : `${Math.floor(h / 24)}일전`;
};

const getDisplay = (item, translated) => {
  if (item.lang === 'ko') return { title: item.title, summary: item.desc || '' };
  // 서버에서 미리 번역된 titleKo 우선 사용, 없으면 클라이언트 번역 캐시
  if (item.titleKo) return { title: item.titleKo, summary: '' };
  const tr = translated[item.title];
  return tr ? { title: tr.t || item.title, summary: tr.s || '' } : { title: item.title, summary: '' };
};

// ========== 필터 ==========
const BREAKING_RULES = [
  // 2026-10-08 추가: 군사 작전 재개·대규모 공습 같은 확전 소식 (CNBC "Iran massive bombing" 이 속보로 안 잡혔던 일)
  { weight: 10, patterns: ['대규모 공습', '공습 재개', '군사 작전 재개', '작전 재개', '지상 작전', '전면전', '선제 타격', 'massive bombing', 'military operations against', 'resume strikes', 'resumption of strikes', 'ground operation', 'pentagon orders', 'pentagon ordered', 'centcom', 'military ordered', 'iran strike', 'strikes on iran', 'strike on iran', 'attack on iran', '공격에 대비', '공격 준비', '타격 준비', '이란 공격'] },
  { weight: 8, patterns: ['공습', '폭격', '미사일 발사', '군사 작전', '파병', '내란법', '비상사태 선포', 'airstrike', 'air strike', 'bombing', 'military strike', 'insurrection act', 'national emergency', 'troops deploy'] },
  { weight: 10, patterns: ['전쟁 종식', '전쟁 종전', '휴전 합의', '휴전 선언', '정전 합의', '평화협정', '핵실험', '핵공격', '침공 개시', '선전포고', 'ceasefire', 'peace deal', 'nuclear war', 'nuclear test', 'nuclear strike', 'invasion'] },
  { weight: 10, patterns: ['서킷브레이커', '사이드카', '블랙먼데이', '사상최고', '사상최저', 'circuit breaker', 'flash crash', 'market crash', 'all-time high', 'all-time low'] },
  { weight: 10, patterns: ['긴급', 'BREAKING', 'URGENT'] },
  { weight: 9, patterns: ['금리인상', '금리인하', '금리동결', '기준금리', 'FOMC', 'rate hike', 'rate cut', 'Fed decision', 'ECB decision'] },
  { weight: 8, patterns: ['유가 폭등', '유가 폭락', '원유 급등', '원유 급락', 'OPEC 감산', 'OPEC 증산', 'oil surge', 'oil crash', 'energy crisis'] },
  { weight: 8, patterns: ['디폴트', '국가부도', '뱅크런', '리먼', '금융위기', 'default', 'bankruptcy', 'bank run', 'financial crisis', 'debt ceiling'] },
  { weight: 7, patterns: ['관세 부과', '관세 철회', '무역전쟁', '수출규제', '경제제재', 'tariff', 'trade war', 'sanctions', 'embargo'] },
  { weight: 7, patterns: ['거래정지', '상장폐지', '인수합병 확정', '파산 신청', '감사보고서 지연', '감사보고서 거절', '감사의견 거절', '감사의견 한정', 'trading halt', 'delisted', 'Chapter 11', 'audit delay', 'audit rejection'] },
];

const isBreaking = (title) => {
  const lower = title.toLowerCase();
  return BREAKING_RULES.some(r => r.patterns.some(p => lower.includes(p.toLowerCase())));
};

const WAR_KEYWORDS = [
  '전쟁', '군대', '군사', '미사일', '폭격', '공습', '포격', '공격', '방어', '국방', '작전',
  '병력', '부대', '해군', '공군', '육군', '해병대', 'NATO', '나토', '무기', '탄도', '드론',
  '전투기', '항공모함', '잠수함', '탱크', '핵무기', '사상자', '민간인', '대피', '폭발', '테러',
  '점령', '침공', '교전', '전선', '우크라이나', '러시아', '이스라엘', '가자', '하마스', '헤즈볼라',
  '이란', '시리아', '대만', '중국', 'military', 'missile', 'bombing', 'airstrike', 'artillery',
  'troops', 'army', 'navy', 'air force', 'weapon', 'drone', 'casualties', 'explosion', 'terror',
  'ukraine', 'russia', 'israel', 'gaza', 'hamas', 'hezbollah', 'iran', 'syria', 'taiwan',
  'pentagon', 'warfare', 'strike', 'offensive', 'defense', 'nato',
];

const CELEB_KEYWORDS = [
  '연예인', '아이돌', '배우', '가수', '드라마', '예능', '출연', 'K-pop', 'kpop',
  'BTS', '블랙핑크', '뮤직비디오', '콘서트', '팬미팅', '음악방송', '시청률',
  '열애', '결혼', '이혼', '스캔들', '루머', '은퇴', '컴백',
];

const matchKeywords = (title, keywords) => {
  const lower = title.toLowerCase();
  return keywords.some(kw => lower.includes(kw.toLowerCase()));
};

// ========== 공통 훅: 뉴스 + 번역 ==========
function useNewsFeed(translateLimit = 10) {
  const [news, setNews] = useState([]);
  const [translated, setTranslated] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const translateCache = useRef({});

  const translateBatch = async (enItems) => {
    if (!enItems.length) return;
    try {
      const res = await fetch(`${BASE}/news-translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titles: enItems.map(n => n.title) }),
      });
      if (!res.ok) return;
      const arr = await res.json();
      if (Array.isArray(arr)) {
        const updated = { ...translateCache.current };
        enItems.forEach((n, i) => {
          // 번역 결과가 있고, 원문과 다를 때만 캐시
          if (arr[i] && arr[i].t && arr[i].t !== n.title) updated[n.title] = arr[i];
        });
        translateCache.current = updated;
        setTranslated({ ...updated });
      }
    } catch {}
  };

  const fetchNews = useCallback(async (skipCache) => {
    // sessionStorage 캐시 (3분, 카테고리 이동 시 즉시 표시)
    if (!skipCache) {
      try {
        const cached = sessionStorage.getItem('news-cache');
        const cachedTime = parseInt(sessionStorage.getItem('news-cache-time') || '0');
        if (cached && Date.now() - cachedTime < 3 * 60 * 1000) {
          const data = JSON.parse(cached);
          setNews(data.news || []);
          setLastUpdate(new Date(data.timestamp));
          setLoading(false);
          return;
        }
      } catch {}
    }
    try {
      const res = await fetch(`${BASE}/news-feed`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.news) {
        setNews(data.news);
        setLastUpdate(new Date());
        try { sessionStorage.setItem('news-cache', JSON.stringify(data)); sessionStorage.setItem('news-cache-time', String(Date.now())); } catch {}
        // 번역 안 된 뉴스만 클라이언트에서 추가 번역
        const enNews = data.news.filter(n => n.lang === 'en' && !n.titleKo && !translateCache.current[n.title]);
        if (enNews.length > 0) await translateBatch(enNews);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchNews();
    const timer = setInterval(() => fetchNews(true), 300000); // 5분마다 강제 갱신
    return () => clearInterval(timer);
  }, [fetchNews]);

  const display = (item) => getDisplay(item, translated);

  return { news, loading, lastUpdate, fetchNews, display };
}

// ========== AI 요약 (기사 펼칠 때) ==========
function useSummary(item) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchSummary = async () => {
    if (summary) { setOpen(!open); return; }
    setOpen(true); setLoading(true); setError(null);
    const attempt = async (retry = 0) => {
      try {
        const res = await fetch(`${BASE}/news-summarize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: item.title, desc: item.desc || '' }),
        });
        const data = await res.json();
        if (res.ok && data.lines) {
          // 요약이 불완전하면 재시도
          const totalLen = data.lines.join('').length;
          const allEndProperly = data.lines.every(l => l.trim().endsWith('니다.') || l.trim().endsWith('니다') || l.trim().endsWith('습니다.') || l.trim().endsWith('있다.') || l.trim().endsWith('했다.') || l.trim().endsWith('됩니다.'));
          const anyShort = data.lines.some(l => l.trim().length < 70);
          const incomplete = data.lines.length < 3 || anyShort || !allEndProperly;
          if (incomplete && retry < 2) return attempt(retry + 1);
          setSummary(data.lines);
        } else {
          setError(data.error || '요약 실패');
        }
      } catch (e) { setError(e.message || '네트워크 오류'); }
    };
    await attempt();
    setLoading(false);
  };

  return { open, summary, loading, error, fetchSummary };
}

// ========== 속보 티커 바 ==========
export default function NewsTicker({ onClickBreaking, onBreakingCount }) {
  const { news, display } = useNewsFeed(10);
  // 번역 제목까지 함께 봄 (영문 원제에만 키워드가 없을 때도 잡히게)
  const breakingNews = news.filter(n => isBreaking(`${n.title} ${n.titleKo || ''}`)).slice(0, 8);

  useEffect(() => {
    if (onBreakingCount) onBreakingCount(breakingNews.length);
  }, [breakingNews.length, onBreakingCount]);

  if (breakingNews.length === 0) return null;

  return (
    <div className="ticker-wrap" onClick={() => onClickBreaking && onClickBreaking()}>
      <div className="ticker-badge">속보</div>
      <div className="ticker-track-wrap">
        <div className="ticker-track" style={{ display: "flex", whiteSpace: "nowrap", animation: `tickerScroll ${Math.max(breakingNews.length * 5, 25)}s linear infinite` }}>
          {[...breakingNews, ...breakingNews].map((item, i) => {
            const d = display(item);
            return (
              <span key={i} style={{ padding: "0 28px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: srcColor(item.source), fontSize: 10, fontWeight: 600 }}>{item.source}</span>
                <span style={{ color: "var(--ticker-text)" }}>{d.title}</span>
                {d.summary && <span style={{ color: "var(--ticker-sub)", fontSize: 11 }}>— {d.summary}</span>}
                <span style={{ color: "#64748B", fontSize: 10 }}>{timeAgo(item.pubDate)}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ========== 글로벌 뉴스 페이지 (2026-10-08 개편) ==========
// 구성: 오늘의 트렌드(키워드 순위) → 탭(전체/속보/국내/해외/관심) → 주제 칩 → 오늘 주요뉴스 카드 → 뉴스 목록
// 분류·종목 태그·중요도는 제목 키워드 규칙으로 계산 (AI 호출 없음). 기사를 누르면 기존처럼 AI 요약

// 주제 분류 (위에서부터 먼저 맞는 것)
const TOPICS = [
  { id: '경제지표', kw: ['CPI', 'PCE', 'PMI', 'GDP', '고용', '실업', '비농업', '소매판매', '물가', '지표', '예상', '이전', 'payroll', 'jobless', 'inflation', 'retail sales'] },
  { id: '금리·연준', kw: ['연준', 'Fed', 'FOMC', '파월', '워시', '금리', '국채', '수익률', 'yield', 'Treasury', 'ECB', '일본은행', 'BOJ', '한국은행', '기준금리'] },
  { id: '에너지', kw: ['유가', '원유', 'WTI', '브렌트', 'OPEC', '천연가스', 'LNG', '송유관', 'oil', 'crude', 'gas price'] },
  { id: '기업·실적', kw: ['실적', '매출', '영업이익', '순이익', '가이던스', '인수', '합병', 'IPO', '상장', '수주', 'earnings', 'revenue', 'guidance', 'acquire', 'merger'] },
  // 주제 분류용: '대만·중국·strike(파업)·공격' 처럼 경제 기사에도 흔한 단어는 뺌
  { id: '전쟁·지정학', kw: WAR_KEYWORDS.filter(k => !['대만', '중국', 'strike', '공격', '방어', 'defense', '작전'].includes(k)) },
  { id: '관세·정책', kw: ['관세', '무역', '제재', '수출규제', '트럼프', '백악관', '의회', '상원', '하원', '중간선거', 'tariff', 'trade', 'sanction', 'Congress', 'Senate', 'White House'] },
  { id: '시황', kw: ['증시', '코스피', '코스닥', '나스닥', 'S&P', '다우', '선물', '마감', '개장', '장중', 'stocks', 'Wall Street', 'futures', 'market'] },
];
const kwHit = (t, k) => /^[A-Za-z&]+$/.test(k) ? new RegExp(`\\b${k.replace('&', '\\&')}\\b`, 'i').test(t) : t.toLowerCase().includes(k.toLowerCase());
const topicOf = (t) => (TOPICS.find(x => x.kw.some(k => kwHit(t, k))) || {}).id || '종합';

// 종목 태그: 제목에 이름이 나오면 칩으로 (미국은 $티커, 국내는 종목명 → 주식 검색으로 이동)
const TICKERS = [
  ['$NVDA', ['엔비디아', 'Nvidia']], ['$AAPL', ['애플', 'Apple']], ['$TSLA', ['테슬라', 'Tesla']], ['$MSFT', ['마이크로소프트', 'Microsoft']],
  ['$AMZN', ['아마존', 'Amazon']], ['$GOOGL', ['알파벳', '구글', 'Alphabet', 'Google']], ['$META', ['메타', 'Meta']], ['$MU', ['마이크론', 'Micron']],
  ['$AMD', ['AMD']], ['$INTC', ['인텔', 'Intel']], ['$TSM', ['TSMC']], ['$AVGO', ['브로드컴', 'Broadcom']], ['$ORCL', ['오라클', 'Oracle']],
  ['$PLTR', ['팔란티어', 'Palantir']], ['$NFLX', ['넷플릭스', 'Netflix']], ['$BA', ['보잉', 'Boeing']], ['$JPM', ['JP모건', 'JPMorgan']],
  ['$GS', ['골드만', 'Goldman']], ['$XOM', ['엑손', 'Exxon']], ['$CVX', ['셰브론', 'Chevron']], ['$COIN', ['코인베이스', 'Coinbase']],
  ['$LMT', ['록히드', 'Lockheed']], ['$MRVL', ['마벨', 'Marvell']], ['$SMCI', ['슈퍼마이크로', 'Supermicro']], ['BTC', ['비트코인', 'Bitcoin']],
];
const KR_STOCKS = [['삼성전자', '005930'], ['SK하이닉스', '000660'], ['현대차', '005380'], ['기아', '000270'], ['LG에너지솔루션', '373220'],
  ['삼성바이오로직스', '207940'], ['셀트리온', '068270'], ['한화에어로스페이스', '012450'], ['HD현대중공업', '329180'], ['한화오션', '042660'],
  ['두산에너빌리티', '034020'], ['네이버', '035420'], ['카카오', '035720'], ['POSCO홀딩스', '005490'], ['KB금융', '105560'], ['삼성SDI', '006400'], ['LG전자', '066570']];
function tagsOf(title, watch) {
  const tags = [];
  // 짧은 이름(AMD, 메타 등)은 앞뒤가 다른 단어에 붙어 있으면 제외
  const has = (n) => n.length > 3 ? title.includes(n) : new RegExp(`(^|[^A-Za-z가-힣])${n}([^A-Za-z]|$)`).test(title);
  TICKERS.forEach(([t, names]) => { if (names.some(has)) tags.push({ label: t }); });
  KR_STOCKS.forEach(([n, code]) => { if (title.includes(n)) tags.push({ label: n, code }); });
  (watch || []).forEach(w => { if (w.name && title.includes(w.name) && !tags.some(t => t.label === w.name)) tags.push({ label: w.name, code: w.code, mine: true }); });
  return tags.slice(0, 4);
}

// 트렌드 키워드 후보 (제목에 나온 횟수로 순위)
const TREND_TERMS = ['연준', '금리', '국채', '유가', '관세', '환율', '금값', '비트코인', '반도체', 'AI', '이란', '이스라엘', '우크라이나', '러시아', '중국', '일본', '트럼프',
  '중간선거', '셧다운', '실적', '고용', '물가', '코스피', '나스닥', ...TICKERS.map(t => t[1][0]), ...KR_STOCKS.map(k => k[0])];

const breakWeight = (t) => Math.max(0, ...BREAKING_RULES.filter(r => r.patterns.some(p => t.toLowerCase().includes(p.toLowerCase()))).map(r => r.weight));

const TABS = ['전체', '속보', '국내', '해외', '관심 뉴스'];
const TOPIC_CHIPS = ['전체', '시황', '경제지표', '금리·연준', '에너지', '관세·정책', '전쟁·지정학', '기업·실적'];

function NewsRow({ item, d, onOpenStock, compact }) {
  const { open, summary, loading, error, fetchSummary } = useSummary(item);
  const isKo = item.lang === 'ko';
  return (
    <div style={{ background: compact ? "transparent" : "var(--bg-card)", borderBottom: compact ? "none" : "1px solid var(--border-light)" }}>
      <div onClick={fetchSummary} style={{ padding: "13px 18px", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontSize: 11 }}>
          <span style={{ padding: "2px 7px", borderRadius: 5, fontWeight: 700, color: srcColor(item.source), background: `${srcColor(item.source)}1f` }}>{item.source}</span>
          {item._breaking && <span style={{ color: "#ef4444", fontWeight: 700 }}>· 속보</span>}
          {item._topic !== '종합' && <span style={{ color: "var(--text-muted)" }}>· {item._topic}</span>}
          <span style={{ marginLeft: "auto", color: "var(--text-muted)", fontFamily: "'DM Mono', monospace" }}>{timeAgo(item.pubDate)}</span>
        </div>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text)", lineHeight: 1.5, wordBreak: "keep-all" }}>{d.title}</div>
        {isKo && item.desc && open === false && <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 4, lineHeight: 1.5 }}>{item.desc.substring(0, 90)}{item.desc.length > 90 ? '…' : ''}</div>}
        {item._tags.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
            {item._tags.map((t, i) => (
              <span key={i} onClick={(e) => { if (t.code && onOpenStock) { e.stopPropagation(); onOpenStock(t.code); } }}
                style={{ fontSize: 11, padding: "2px 7px", borderRadius: 5, fontFamily: t.label.startsWith('$') ? "'DM Mono', monospace" : "inherit", color: t.mine ? "var(--gold)" : "var(--text-sub)", background: t.mine ? "var(--gold-bg)" : "var(--bg-hover)", border: "1px solid var(--border-light)", cursor: t.code ? "pointer" : "default" }}>{t.label}</span>
            ))}
          </div>
        )}
      </div>
      {open && (
        <div style={{ padding: "0 18px 16px" }}>
          <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.18)" }}>
            <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 700, marginBottom: 6 }}>AI 요약</div>
            {loading && <div style={{ fontSize: 12, color: "var(--text-sub)" }}>요약 생성 중...</div>}
            {error && <div style={{ fontSize: 12, color: "#fb7185" }}>{error}</div>}
            {summary?.map((line, i) => <div key={i} style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.65, marginBottom: 3 }}>· {line}</div>)}
          </div>
          {!isKo && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>원문: {item.title}</div>}
          {item.link && <a href={item.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ display: "inline-block", marginTop: 8, fontSize: 12, color: "var(--link)", textDecoration: "none" }}>기사 원문 보기 →</a>}
        </div>
      )}
    </div>
  );
}

export function NewsPage({ watchlist, onOpenStock }) {
  const { news, loading, lastUpdate, fetchNews, display } = useNewsFeed(15);
  const [tab, setTab] = useState('전체');
  const [topic, setTopic] = useState('전체');
  const [src, setSrc] = useState('전체');
  const [sort, setSort] = useState('최신순');
  const [kw, setKw] = useState(null);
  const [showTop, setShowTop] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [limit, setLimit] = useState(40);
  const seenRef = useRef(null);
  const watch = (watchlist || []).filter(w => w && w.code && !w._type);

  // 제목(한글 번역 우선)으로 분류·태그·중요도 계산
  const items = news.filter(n => !matchKeywords(n.title, CELEB_KEYWORDS)).map(n => {
    const d = display(n);
    const t = `${d.title} ${n.title}`;
    const w = breakWeight(t);
    const tags = tagsOf(d.title + ' ' + n.title, watch);
    const hours = n.pubDate ? (Date.now() - new Date(n.pubDate).getTime()) / 3600000 : 99;
    return { ...n, _d: d, _breaking: w > 0, _topic: topicOf(t), _tags: tags, _mine: tags.some(x => x.mine), _hours: hours, _score: w * 2 + (tags.length ? 3 : 0) + (tags.some(x => x.mine) ? 4 : 0) - Math.min(hours, 24) / 3 };
  });

  // 새 뉴스 개수 (갱신 때 처음 보는 제목)
  useEffect(() => {
    if (!news.length) return;
    const keys = new Set(news.map(n => n.title));
    if (seenRef.current) { let c = 0; keys.forEach(k => { if (!seenRef.current.has(k)) c++; }); if (c) setNewCount(c); }
    seenRef.current = keys;
  }, [news]);

  const day = items.filter(n => n._hours <= 24);
  const trends = TREND_TERMS.map(term => ({ term, n: day.filter(x => x._d.title.includes(term) || x.title.includes(term)).length })).filter(x => x.n >= 2).sort((a, b) => b.n - a.n).slice(0, 6);
  // 주요뉴스는 한 출처가 독차지하지 않게 출처당 2건까지
  const perSrc = {};
  const top = [...day].sort((a, b) => b._score - a._score).filter(n => (perSrc[n.source] = (perSrc[n.source] || 0) + 1) <= 2).slice(0, 6);
  const sources = ['전체', ...Array.from(new Set(items.map(n => n.source)))];

  let list = items;
  if (tab === '속보') list = list.filter(n => n._breaking);
  if (tab === '국내') list = list.filter(n => n.lang === 'ko');
  if (tab === '해외') list = list.filter(n => n.lang !== 'ko');
  if (tab === '관심 뉴스') list = list.filter(n => n._mine);
  if (topic !== '전체') list = list.filter(n => n._topic === topic);
  if (src !== '전체') list = list.filter(n => n.source === src);
  if (kw) list = list.filter(n => n._d.title.includes(kw) || n.title.includes(kw));
  if (sort === '중요도순') list = [...list].sort((a, b) => b._score - a._score);

  const refresh = async () => { setRefreshing(true); setNewCount(0); await fetchNews(true); setRefreshing(false); };
  const sel = { padding: "5px 8px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)", fontSize: 12 };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "var(--text-sub)" }}>뉴스 로딩 중...</div>;

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      {/* 오늘의 트렌드 */}
      {trends.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "10px 14px", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12, marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)", marginRight: 4 }}><Ico n="flame" size="1em" />오늘의 트렌드</span>
          {trends.map((x, i) => (
            <button key={x.term} onClick={() => setKw(kw === x.term ? null : x.term)} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, padding: "4px 9px", borderRadius: 999, cursor: "pointer", border: "1px solid " + (kw === x.term ? "var(--primary)" : "var(--border-light)"), background: kw === x.term ? "var(--sb-active-bg)" : "transparent", color: "var(--text)" }}>
              <b style={{ fontFamily: "'DM Mono', monospace", color: i < 3 ? "#ef4444" : "var(--text-muted)" }}>{i + 1}</b>{x.term}<span style={{ fontSize: 10.5, color: "var(--text-muted)" }}>{x.n}</span>
            </button>
          ))}
          {kw && <button onClick={() => setKw(null)} style={{ fontSize: 11, border: "none", background: "transparent", color: "var(--link)", cursor: "pointer" }}>필터 해제</button>}
        </div>
      )}

      {/* 탭 */}
      <div style={{ display: "flex", gap: 18, borderBottom: "1px solid var(--border)", marginBottom: 12 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: "8px 2px", border: "none", background: "transparent", cursor: "pointer", fontSize: 14.5, fontWeight: tab === t ? 700 : 500, color: tab === t ? "var(--text)" : "var(--text-muted)", borderBottom: tab === t ? "2px solid var(--text)" : "2px solid transparent", marginBottom: -1 }}>
            {t}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
        {TOPIC_CHIPS.map(t => (
          <button key={t} onClick={() => setTopic(t)} style={{ fontSize: 12.5, padding: "6px 13px", borderRadius: 999, cursor: "pointer", border: "1px solid " + (topic === t ? "var(--text)" : "var(--border)"), background: topic === t ? "var(--text)" : "var(--bg-card)", color: topic === t ? "var(--bg-base)" : "var(--text-sub)", fontWeight: topic === t ? 700 : 500 }}>{t}</button>
        ))}
      </div>

      {/* 오늘 주요뉴스 */}
      {top.length > 0 && tab === '전체' && topic === '전체' && !kw && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}><Ico n="flame" />오늘 주요뉴스</div>
            <button onClick={() => setShowTop(!showTop)} style={{ border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-sub)", borderRadius: 999, fontSize: 11, padding: "3px 10px", cursor: "pointer" }}>{showTop ? '접기' : '펼치기'}</button>
          </div>
          {showTop && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
              {top.map((n, i) => (
                <div key={i} style={{ background: "var(--sb-active-bg)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
                  <NewsRow item={n} d={n._d} onOpenStock={onOpenStock} compact />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 뉴스 목록 */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)", marginRight: "auto" }}>뉴스 <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>{list.length}건</span></div>
        <select value={src} onChange={e => setSrc(e.target.value)} style={sel}>{sources.map(s => <option key={s} value={s}>{s === '전체' ? '출처 전체' : s}</option>)}</select>
        <select value={sort} onChange={e => setSort(e.target.value)} style={sel}>{['최신순', '중요도순'].map(s => <option key={s}>{s}</option>)}</select>
        <button onClick={refresh} title="새로고침" style={{ ...sel, cursor: "pointer" }}>{refreshing ? '갱신 중…' : '새로고침'}</button>
      </div>
      {newCount > 0 && (
        <button onClick={() => { setNewCount(0); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ display: "block", margin: "0 auto 10px", fontSize: 12, padding: "5px 14px", borderRadius: 999, border: "none", background: "var(--primary)", color: "#fff", cursor: "pointer" }}>새 뉴스 {newCount}건</button>
      )}
      <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        {list.slice(0, limit).map(n => <NewsRow key={n.title + n.source} item={n} d={n._d} onOpenStock={onOpenStock} />)}
        {list.length === 0 && <div style={{ padding: 50, textAlign: "center", color: "var(--text-sub)", fontSize: 13, background: "var(--bg-card)" }}>{tab === '관심 뉴스' && watch.length === 0 ? '주식 검색에서 관심종목을 추가하면 관련 뉴스가 여기에 모입니다' : '조건에 맞는 뉴스가 없습니다'}</div>}
      </div>
      {list.length > limit && <button onClick={() => setLimit(limit + 40)} style={{ display: "block", margin: "12px auto", ...sel, cursor: "pointer" }}>더 보기</button>}
      <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", margin: "14px 0 24px" }}>
        5분마다 자동 갱신{lastUpdate && !isNaN(lastUpdate) ? ` · 마지막 갱신 ${lastUpdate.toLocaleTimeString("ko-KR")}` : ''} · 해외 기사 제목은 자동 번역 · 기사를 누르면 AI 요약
      </div>
    </div>
  );
}
